import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useClient } from "../context/ClientContext";
import { PageHeader, Card, Pill, LoadingBar } from "../components/Primitives";
import { ReactFlow, Background, Controls, MiniMap, MarkerType } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { X, Sparkle, CircleNotch, Plus } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";

const ROLE_BG = {
  system: "#FFFFFF", control: "#E6F7FE", handoff: "#FFF1F2", output: "#F0FDF4",
};
const ROLE_BORDER = {
  system: "#0F172A", control: "#00AEEF", handoff: "#E11D48", output: "#16A34A",
};
const ROLE_TEXT = {
  system: "#0F172A", control: "#005577", handoff: "#9F1239", output: "#166534",
};

const RISK_EDGE = { low: "#A1A1AA", medium: "#F59E0B", high: "#E11D48" };

function CoverageNode({ data }) {
  const cov = data.coverage ?? 0;
  const count = data.scenario_count ?? 0;
  const covColor = cov >= 75 ? "#16A34A" : cov >= 35 ? "#F59E0B" : count === 0 ? "#E11D48" : "#52525B";
  return (
    <div
      style={{
        background: ROLE_BG[data.role] || "#fff",
        border: `1.5px solid ${ROLE_BORDER[data.role] || "#0F172A"}`,
        color: ROLE_TEXT[data.role] || "#0F172A",
        padding: "10px 14px", minWidth: 180, borderRadius: 2,
        fontSize: 12, fontFamily: "IBM Plex Sans", cursor: "pointer",
      }}
    >
      <div className="font-medium">{data.label}</div>
      <div className="mt-2 flex items-center justify-between gap-2">
        <span style={{ fontSize: 10, fontFamily: "JetBrains Mono", color: "#71717A" }}>
          {count} scenario{count !== 1 ? "s" : ""}
        </span>
        <div className="flex items-center gap-1">
          {data.priorities?.P0 > 0 && (
            <span style={{ background: "#E11D48", color: "#fff", fontSize: 9, padding: "1px 4px", fontFamily: "JetBrains Mono" }}>P0×{data.priorities.P0}</span>
          )}
        </div>
      </div>
      <div style={{ marginTop: 6, height: 3, background: "#F4F4F5" }}>
        <div style={{ height: "100%", width: `${cov}%`, background: covColor }} />
      </div>
    </div>
  );
}

const nodeTypes = { coverage: CoverageNode };

function buildGraph(workflow, coverageMap) {
  const nodes = workflow.nodes.map((n) => ({
    id: n.id,
    type: "coverage",
    position: n.position,
    data: {
      label: n.label, role: n.role,
      coverage: coverageMap[n.id]?.coverage ?? 0,
      scenario_count: coverageMap[n.id]?.scenario_count ?? 0,
      priorities: coverageMap[n.id]?.priorities,
    },
  }));
  const edges = workflow.edges.map((e) => ({
    id: e.id, source: e.source, target: e.target, type: "smoothstep",
    animated: e.risk === "high",
    style: { stroke: RISK_EDGE[e.risk] || "#A1A1AA", strokeWidth: e.risk === "high" ? 2 : 1.2 },
    label: e.risk === "high" ? "HIGH RISK" : "",
    labelStyle: { fill: "#9F1239", fontSize: 10, fontFamily: "JetBrains Mono", letterSpacing: "0.1em" },
    labelBgStyle: { fill: "#FFF1F2" }, labelBgPadding: [4, 2],
    markerEnd: { type: MarkerType.ArrowClosed, color: RISK_EDGE[e.risk] || "#A1A1AA" },
  }));
  return { nodes, edges };
}

const PRIO_TONE = { P0: "danger", P1: "warn", P2: "neutral" };

export default function WorkflowGraph() {
  const { activeClient, activeClientId } = useClient();
  const [list, setList] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [workflow, setWorkflow] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [nodeDrawer, setNodeDrawer] = useState(null);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (!activeClientId) return;
    setList([]); setSelectedId(null); setWorkflow(null); setCoverage(null);
    api.get("/workflows", { params: { client_id: activeClientId } }).then((r) => {
      setList(r.data);
      if (r.data.length) setSelectedId(r.data[0].id);
    });
  }, [activeClientId]);

  async function loadWorkflow(id) {
    const [wfRes, covRes] = await Promise.all([
      api.get(`/workflows/${id}`),
      api.get(`/workflows/${id}/coverage`),
    ]);
    setWorkflow(wfRes.data); setCoverage(covRes.data); setNodeDrawer(null);
  }

  useEffect(() => { if (selectedId) loadWorkflow(selectedId); }, [selectedId]);

  const coverageMap = useMemo(() => {
    const m = {};
    (coverage?.nodes || []).forEach((n) => (m[n.id] = n));
    return m;
  }, [coverage]);

  const { nodes, edges } = useMemo(
    () => (workflow ? buildGraph(workflow, coverageMap) : { nodes: [], edges: [] }),
    [workflow, coverageMap]
  );

  function handleNodeClick(_e, node) {
    const cov = coverageMap[node.id];
    if (cov) setNodeDrawer(cov);
  }

  async function generateForNode() {
    if (!nodeDrawer || !workflow) return;
    setGenerating(true);
    try {
      await api.post("/scenarios/generate", {
        workflow_id: workflow.id,
        change_delta: `Generate scenarios specifically for the "${nodeDrawer.label}" step.`,
        focus: `Coverage gap at ${nodeDrawer.label} (${nodeDrawer.role}); include edge + handoff failures.`,
      });
      await loadWorkflow(workflow.id);
    } finally { setGenerating(false); }
  }

  return (
    <div className="h-full flex flex-col" data-testid="workflow-graph-page">
      <PageHeader
        overline={`Layer 2 · ${activeClient?.name || ""} / ${activeClient?.platform || ""}`}
        title="Workflow Graph"
        description="Semantic map of business workflows. Click a step to see its test-scenario coverage and ask the AI to fill gaps."
        right={coverage ? (
          <div className="flex items-center gap-2">
            <Pill tone="info">{coverage.node_coverage_pct}% nodes covered</Pill>
            <Pill tone="neutral">{coverage.total_scenarios} scenarios</Pill>
          </div>
        ) : null}
      />
      <div className="flex-1 grid grid-cols-12 min-h-0">
        <div className="col-span-12 lg:col-span-3 border-r border-zinc-200 bg-white overflow-y-auto" data-testid="workflow-list">
          <div className="px-6 pt-6 pb-3 overline text-zinc-500 sticky top-0 bg-white border-b border-zinc-100">
            Workflows ({list.length})
          </div>
          {list.map((w) => (
            <button
              key={w.id}
              data-testid={`workflow-item-${w.id}`}
              onClick={() => setSelectedId(w.id)}
              className={`w-full text-left px-6 py-4 border-b border-zinc-100 hover:bg-zinc-50 transition-colors ${
                selectedId === w.id ? "bg-vlink-50 border-l-2 border-l-vlink-500" : "border-l-2 border-l-transparent"
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <div className="text-sm font-medium text-zinc-900">{w.name}</div>
                <Pill tone={w.criticality === "high" ? "danger" : w.criticality === "medium" ? "warn" : "neutral"}>
                  {w.criticality}
                </Pill>
              </div>
              <div className="text-xs text-zinc-500">{w.owner}</div>
              <div className="mt-2 flex items-center gap-2">
                <div className="flex-1 h-1 bg-zinc-100"><div className="h-full bg-vlink-500" style={{ width: `${w.stability}%` }} /></div>
                <span className="text-[11px] font-mono text-zinc-700">{w.stability}%</span>
              </div>
            </button>
          ))}
        </div>

        <div className="col-span-12 lg:col-span-9 relative bg-zinc-50 min-h-0" data-testid="workflow-canvas">
          {!workflow ? (
            <LoadingBar label="Loading workflow graph" />
          ) : (
            <>
              <div className="absolute z-10 top-4 left-4 right-4 flex items-start justify-between gap-4 pointer-events-none">
                <Card className="p-4 max-w-md pointer-events-auto" testid="workflow-info">
                  <div className="overline text-zinc-500 mb-1">Selected workflow</div>
                  <div className="font-heading text-xl text-zinc-900">{workflow.name}</div>
                  <div className="mt-2 text-xs text-zinc-600 leading-relaxed">
                    <span className="font-medium text-rose-700">Handoff risks: </span>{workflow.handoff_risks}
                  </div>
                </Card>
                <Card className="p-3 pointer-events-auto" testid="workflow-legend">
                  <div className="overline text-zinc-500 mb-2">Coverage</div>
                  <div className="flex flex-col gap-1 text-[11px] font-mono">
                    <div className="flex items-center gap-2"><span className="inline-block w-4 h-1 bg-emerald-600" /> ≥75%</div>
                    <div className="flex items-center gap-2"><span className="inline-block w-4 h-1 bg-amber-500" /> 35–74%</div>
                    <div className="flex items-center gap-2"><span className="inline-block w-4 h-1 bg-rose-600" /> 0 / gap</div>
                  </div>
                </Card>
              </div>
              <ReactFlow
                nodes={nodes} edges={edges}
                nodeTypes={nodeTypes}
                fitView proOptions={{ hideAttribution: true }}
                onNodeClick={handleNodeClick}
              >
                <Background color="#E4E4E7" gap={24} />
                <Controls showInteractive={false} />
                <MiniMap pannable zoomable nodeStrokeWidth={2} />
              </ReactFlow>

              <AnimatePresence>
                {nodeDrawer && (
                  <motion.aside
                    initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
                    transition={{ type: "tween", duration: 0.22 }}
                    className="absolute right-0 top-0 bottom-0 w-[420px] bg-white border-l border-zinc-200 shadow-xl flex flex-col z-20"
                    data-testid="node-drawer"
                  >
                    <div className="px-6 py-5 border-b border-zinc-200 flex items-start justify-between gap-3">
                      <div>
                        <div className="overline text-zinc-500">Step coverage</div>
                        <div className="font-heading text-lg text-zinc-900 mt-1">{nodeDrawer.label}</div>
                        <div className="text-xs text-zinc-500 font-mono mt-0.5">role: {nodeDrawer.role}</div>
                      </div>
                      <button onClick={() => setNodeDrawer(null)} data-testid="close-drawer" className="text-zinc-400 hover:text-zinc-900">
                        <X size={18} />
                      </button>
                    </div>

                    <div className="px-6 py-4 border-b border-zinc-100">
                      <div className="grid grid-cols-3 gap-3 text-center">
                        <div>
                          <div className="overline text-zinc-500">Coverage</div>
                          <div className="metric-value text-2xl text-zinc-900 mt-1">{nodeDrawer.coverage}<span className="text-zinc-400 text-xs font-mono">%</span></div>
                        </div>
                        <div>
                          <div className="overline text-zinc-500">Scenarios</div>
                          <div className="metric-value text-2xl text-zinc-900 mt-1">{nodeDrawer.scenario_count}</div>
                        </div>
                        <div>
                          <div className="overline text-zinc-500">P0</div>
                          <div className="metric-value text-2xl text-rose-700 mt-1">{nodeDrawer.priorities?.P0 || 0}</div>
                        </div>
                      </div>
                      <button
                        data-testid="generate-for-node-btn"
                        onClick={generateForNode}
                        disabled={generating}
                        className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-vlink-500 hover:bg-vlink-600 text-white text-sm font-medium px-4 py-2.5 rounded-sm transition-colors disabled:opacity-60"
                      >
                        {generating ? <CircleNotch size={14} className="animate-spin" /> : <Sparkle size={14} />}
                        {generating ? "Generating…" : "Generate AI scenarios for this step"}
                      </button>
                    </div>

                    <div className="flex-1 overflow-y-auto px-6 py-4">
                      <div className="overline text-zinc-500 mb-3">Mapped scenarios ({nodeDrawer.scenarios?.length || 0})</div>
                      {(!nodeDrawer.scenarios || nodeDrawer.scenarios.length === 0) ? (
                        <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 px-3 py-3 rounded-sm flex items-start gap-2">
                          <Plus size={14} className="mt-0.5" />
                          <span>Coverage gap. No test scenarios are mapped to this step yet — generate above or ingest a requirements doc.</span>
                        </div>
                      ) : (
                        <ul className="space-y-2">
                          {nodeDrawer.scenarios.map((s) => (
                            <li key={s.id} className="border border-zinc-200 px-3 py-2.5 hover:bg-zinc-50">
                              <div className="flex items-center justify-between gap-2 mb-1">
                                <div className="text-sm text-zinc-900 leading-snug">{s.title}</div>
                                <Pill tone={PRIO_TONE[s.priority] || "neutral"}>{s.priority}</Pill>
                              </div>
                              <div className="flex items-center gap-2 text-[11px] text-zinc-500 font-mono">
                                <span>{s.type}</span>
                                {s.ai_generated && <span className="text-blue-700">· AI</span>}
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </motion.aside>
                )}
              </AnimatePresence>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
